import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Menu,
  OutlinedInput,
  Stack,
  Typography,
  Modal,
  Slide,
  Tabs,
  Tab,
  CircularProgress,
  Collapse,
} from "@mui/material";
import { useLocation, Outlet, useNavigate, useNavigationType } from "react-router-dom";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import LogoIcon from "../../vendor/_new/image/logo.svg";
import LayerdIcon from "../../components/common/LayerdIcon";
import { V2_NAV_ITEMS as Items } from "../../icons/v2NavItems";
import {
  Building2,
  FolderKanban,
  User,
  LogOut,
  Bell,
  X,
  Check,
  Plus,
  Search,
  CircleHelp,
  BookOpen,
  Award as LicenseIcon,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  Bug,
} from "lucide-react";
import GlobalSiteSelector from "../../components/common/GlobalSiteSelector";
import { isSiteSelectorLockedPath, isSiteSelectorHiddenPath } from "../../utils/siteSelectorRoutes";
import {
  isAllSites,
  pickDefaultSiteId,
  requiresSingleSiteScopePath,
} from "../../utils/globalSite";
import { HowToWorkProvider } from "./HowToWorkContext";
import { HowToWorkHeaderTrigger, HowToWorkPanel, HowToWorkVideoModal } from "./HowToWork";

import colors from "../../utils/colors";
import "../../vendor/perfect-scrollbar.css";

import useAuth from "../../hooks/useAuth";
import useHome from "../../hooks/user/useHome";
import useSite from "../../hooks/user/useSite";
import usePrefetch from "../../hooks/usePrefetch";
import usePermissions from "../../hooks/usePermissions";
import { useEffectiveFeatures, useEffectiveFeaturesState } from "../../contexts/user/EffectiveFeaturesContext";
import userAxios from "../../utils/axios/v1/userAxios";
import GlobalSearchModal from "../../components/common/GlobalSearch";
import LicenseBreachBanner from "../../components/pages/application/LicenseBreachBanner";
import ReportBugDrawer from "../../components/common/ReportBugDrawer";
import ReleaseNotesDrawer from "../../components/common/ReleaseNotesDrawer";
import { installConsoleErrorBuffer } from "../../utils/consoleErrorBuffer";
import { useNotification } from "../../contexts/NotificationContext";

const SIDEBAR_NAV_IDLE_COLOR = colors.neutral[700];
const SIDEBAR_SUBMENU_IDLE_COLOR = colors.neutral[700];

function SidebarNavIcon({ icon, active, size = 20 }) {
  return (
    <LayerdIcon
      icon={icon}
      size={size}
      color={active ? "primaryLight" : SIDEBAR_NAV_IDLE_COLOR}
    />
  );
}

function isFlyoutMenuActive(currentRoute, menuPath) {
  if (!menuPath) return false;
  const [menuPathname, menuSearch = ""] = menuPath.split("?");
  const [routePathname, routeSearch = ""] = (currentRoute || "").split("?");
  if (menuSearch) {
    return routePathname === menuPathname && routeSearch === menuSearch;
  }
  return routePathname === menuPathname;
}

function isMainNavRouteActive(currentRoute, itemPath) {
  if (!itemPath) return false;
  return currentRoute === itemPath || currentRoute.startsWith(`${itemPath}/`);
}

function getSidebarNavItemSx({ isRouteActive, isFlyoutOpen, isHovered }) {
  const isEmphasized = isRouteActive || isFlyoutOpen || isHovered;
  return {
    textDecoration: "none",
    cursor: "pointer",
    alignSelf: "stretch",
    width: "100%",
    maxWidth: 64,
    boxSizing: "border-box",
    px: 0.5,
    py: 0.5,
    ...(isEmphasized && {
      "& .sidebar-nav-label": {
        color: colors.primary[300],
      },
    }),
  };
}


// Compact "X seconds/minutes/hours/days ago" formatter for the bell panel.
function _relativeTime(d) {
  if (!d) return "";
  const t = typeof d === "string" ? new Date(d).getTime() : d.getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 60)       return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)       return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24)       return `${h} hour${h === 1 ? "" : "s"} ago`;
  const day = Math.floor(h / 24);
  if (day < 7)      return `${day} day${day === 1 ? "" : "s"} ago`;
  return new Date(t).toLocaleDateString();
}


const NotificationTabs = {
  Unread: "Unread",
  Read: "Read",
  All: "All",
};

const DOCUMENTATION_URL = "https://support.layerd.com/";

export const generateMenuSx = (width, height = 32) => {
  return {
    width,
    height,
    p: 2,
    cursor: "pointer",
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: colors.neutral[50],
    },
    "& svg": {
      color: colors.neutral[600],
    },
  };
};

export const stringAvatar = (name) => {
  if (!name || name.split(" ").length < 2) {
    return { children: name ? name[0] : "A" };
  }
  return {
    children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
  };
};

const NewNormalLayout = ({ children }) => {
  const navigate = useNavigate();
  const router = useLocation();
  const currentRoute = router.pathname;
  const navigationType = useNavigationType();
  const mainScrollRef = useRef(null);
  const scrollPositions = useRef({});

  useLayoutEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;

    if (navigationType === "POP") {
      const saved = scrollPositions.current[currentRoute];
      el.scrollTop = saved ?? 0;
    } else {
      el.scrollTop = 0;
    }
    window.scrollTo(0, 0);

    return () => {
      if (mainScrollRef.current) {
        scrollPositions.current[currentRoute] = mainScrollRef.current.scrollTop;
      }
    };
  }, [currentRoute, navigationType]);

  const {
    user,
    signOut,
    notifications,
    getNotifications4User,
    markNotificationAsRead,
    markAllNotificationAsRead,
  } = useAuth();
  const { sauser } = useAuth();
  const { hasPermission, hasRolePermission, isAdmin, PERMISSIONS } = usePermissions();
  const { showSuccess } = useNotification();
  // Licensing-v2 effective-features map. `loaded === false` means we're still
  // fetching — don't hide nav items in that window (avoids a flash where the
  // API Security menu briefly collapses then re-expands on load).
  const effectiveFeatures = useEffectiveFeatures();
  const { loaded: effectiveFeaturesLoaded } = useEffectiveFeaturesState();
  const {
    projectList,
    structedProjectList,
    curProjectId,
    setCurProjectId,
    getAllProjectBaseList,
    wafOverview,
    getWafOverview,
    setSiteSelectorEnabled,
    siteSelectorEnabled,
    curSiteId,
    setCurSiteId,
    sitesFromProjectId,
  } = useHome();
  const { siteList, getSitesForItems } = useSite();
  const { 
    prefetchOverview, 
    prefetchSitesPage, 
    prefetchServices, 
    prefetchDetection,
    prefetchCerts,
    prefetchFindings,
    prefetchSettings,
  } = usePrefetch(
    getSitesForItems,
    siteList,
    getAllProjectBaseList,
    projectList,
    getWafOverview,
    wafOverview
  );

  const getPrefetchHandler = (title) => {
    switch (title) {
      case "Overview":
        return prefetchOverview;
      case "Sites":
        return prefetchSitesPage;
      case "Services":
        return prefetchServices;
      case "Detection":
        return prefetchDetection;
      case "SSL/TLS":
        return prefetchCerts;
      case "Findings":
        return prefetchFindings;
      case "Settings":
        return prefetchSettings;
      default:
        return null;
    }
  };

  const [projects, setProjects] = useState(null);
  const [pattern, setPattern] = useState("");

  useEffect(() => {
    // SSL/TLS pages control site scope per route (overview vs configuration).
    if (currentRoute?.startsWith?.("/v2/ssl-tls") || currentRoute?.startsWith?.("/v2/certs")) return;
    setSiteSelectorEnabled(!isSiteSelectorLockedPath(currentRoute));
  }, [currentRoute, setSiteSelectorEnabled]);

  // Per-site service pages need a concrete site — restore last pick or default to first.
  useLayoutEffect(() => {
    if (currentRoute?.startsWith?.("/v2/ssl-tls") || currentRoute?.startsWith?.("/v2/certs")) return;
    if (!siteSelectorEnabled || !requiresSingleSiteScopePath(currentRoute)) return;
    if (!isAllSites(curSiteId)) return;
    const options = sitesFromProjectId?.[curProjectId] ?? [];
    const next = pickDefaultSiteId(options);
    if (next) setCurSiteId(next);
  }, [
    currentRoute,
    siteSelectorEnabled,
    curSiteId,
    curProjectId,
    sitesFromProjectId,
    setCurSiteId,
  ]);
  const [organisationDisplayName, setOrganisationDisplayName] = useState("My Organisation");
  const [showTenantIndicator, setShowTenantIndicator] = useState(false);
  
  // Filter menu items based on permissions
  const filteredItems = useMemo(() => {
    // Admin users see all menu items
    if (isAdmin()) {
      return Items;
    }
    
    return Items.map(item => {
      // Sites menu - requires sites:read permission
      if (item.title === "Sites") {
        if (!hasPermission(PERMISSIONS.SITES_READ)) {
          return null;
        }
      }
      
      // SSL/TLS menu - requires ssl:read permission
      if (item.title === "SSL/TLS") {
        if (!hasPermission(PERMISSIONS.SSL_READ)) {
          return null;
        }
      }
      
      // Findings menu - requires dast:read permission
      if (item.title === "Findings") {
        if (!hasPermission(PERMISSIONS.DAST_READ)) {
          return null;
        }
      }
      
      // Handle Detection submenu
      if (item.title === "Detection") {
        const filteredSubItems = item.subItems.map(subItem => {
          if (subItem.title === "Web application firewall") {
            const filteredMenus = subItem.menus.filter(menu => {
              if (menu.title === "WAF event") {
                return hasPermission(PERMISSIONS.WAF_READ);
              }
              if (menu.title === "Dashboard") {
                return hasPermission(PERMISSIONS.WAF_READ) || hasPermission(PERMISSIONS.STATS_READ);
              }
              return true;
            });
            return filteredMenus.length > 0 ? { ...subItem, menus: filteredMenus } : null;
          }
          if (subItem.title === "Bot management") {
            if (!hasPermission(PERMISSIONS.BOT_READ)) return null;
            return subItem;
          }
          if (subItem.title === "Rate limiting") {
            if (!hasPermission(PERMISSIONS.RATE_LIMIT_READ)) return null;
            return subItem;
          }
          if (subItem.title === "Firewall") {
            if (!hasPermission(PERMISSIONS.FIREWALL_READ)) return null;
            return subItem;
          }
          return subItem;
        }).filter(Boolean);
        
        // Keep Detection menu even if empty, but with empty subItems
        return { ...item, subItems: filteredSubItems };
      }
      
      // Handle Services submenu
      if (item.title === "Services") {
        const filteredSubItems = item.subItems.map(subItem => {
          if (subItem.title === "Web application firewall") {
            if (!hasPermission(PERMISSIONS.WAF_READ) && !hasPermission(PERMISSIONS.WAF_CONFIGURE)) return null;
            const filteredMenus = subItem.menus.filter(menu => {
              if (menu.title === "Custom rules") {
                return hasPermission(PERMISSIONS.RULES_READ) || hasPermission(PERMISSIONS.RULES_WRITE);
              }
              return true;
            });
            return { ...subItem, menus: filteredMenus };
          }
          if (subItem.title === "Rate limiting") {
            if (!hasPermission(PERMISSIONS.RATE_LIMIT_READ) && !hasPermission(PERMISSIONS.RATE_LIMIT_WRITE)) return null;
            return subItem;
          }
          if (subItem.title === "Firewall") {
            if (!hasPermission(PERMISSIONS.FIREWALL_READ) && !hasPermission(PERMISSIONS.FIREWALL_WRITE)) return null;
            return subItem;
          }
          if (subItem.title === "Bot") {
            if (!hasPermission(PERMISSIONS.BOT_READ) && !hasPermission(PERMISSIONS.BOT_CONFIGURE)) return null;
            return subItem;
          }
          if (subItem.title === "DDos") {
            if (!hasPermission(PERMISSIONS.DDOS_READ) && !hasPermission(PERMISSIONS.DDOS_CONFIGURE)) return null;
            return subItem;
          }
          return subItem;
        }).filter(Boolean);
        
        // Keep Services menu even if empty, but with empty subItems
        return { ...item, subItems: filteredSubItems };
      }
      
      // Handle Settings submenu
      if (item.title === "Settings") {
        const filteredSubItems = item.subItems.map(subItem => {
          if (subItem.title === "Account") {
            const filteredMenus = subItem.menus.filter(menu => {
              if (menu.title === "User Management" || menu.title === "Role management") {
                // Org user/role screens: require capability on the account role only (not project grants)
                return (
                  hasRolePermission(PERMISSIONS.USERS_READ) ||
                  hasRolePermission(PERMISSIONS.USERS_MANAGE)
                );
              }
              if (menu.title === "Organisation settings") {
                return hasPermission(PERMISSIONS.SETTINGS_READ) || hasPermission(PERMISSIONS.SETTINGS_WRITE);
              }
              return true; // Account management is always visible
            });
            return filteredMenus.length > 0 ? { ...subItem, menus: filteredMenus } : null;
          }
          if (subItem.title === "Security") {
            if (!hasPermission(PERMISSIONS.SSO_READ) && !hasPermission(PERMISSIONS.SSO_CONFIGURE)) return null;
            return subItem;
          }
          if (subItem.title === "Integrations") {
            const filteredMenus = subItem.menus.filter(menu => {
              if (menu.title === "Integrations") {
                return hasPermission(PERMISSIONS.NOTIFICATIONS_READ) || hasPermission(PERMISSIONS.NOTIFICATIONS_MANAGE);
              }
              return true;
            });
            return filteredMenus.length > 0 ? { ...subItem, menus: filteredMenus } : null;
          }
          if (subItem.title === "Compliance") {
            const filteredMenus = subItem.menus.filter(menu => {
              if (menu.title === "Audit Logs") {
                return hasPermission(PERMISSIONS.LOGS_READ);
              }
              return true;
            });
            return filteredMenus.length > 0 ? { ...subItem, menus: filteredMenus } : null;
          }
          if (subItem.title === "Project") {
            if (!hasPermission(PERMISSIONS.PROJECTS_READ) && !hasPermission(PERMISSIONS.PROJECTS_WRITE)) return null;
            return subItem;
          }
          return subItem;
        }).filter(Boolean);
        
        // Keep Settings menu even if empty, but with empty subItems
        return { ...item, subItems: filteredSubItems };
      }
      
      // API Security — any api_security:* grant shows the section
      if (item.title === "API Security") {
        const canAccess = [
          PERMISSIONS.API_SECURITY_READ,
          PERMISSIONS.API_SECURITY_CONFIGURE,
          PERMISSIONS.API_SECURITY_WRITE,
          PERMISSIONS.API_SECURITY_DELETE,
        ].some((p) => hasPermission(p));
        if (!canAccess) return null;

        // Licensing-v2 — hide menus whose featureKey is explicitly licensed-off.
        // Menus without a featureKey (Overview, Events) are always shown. While
        // the effective-features map is still loading we leave everything
        // visible to avoid a flash on first paint.
        if (effectiveFeaturesLoaded) {
          const filteredSubItems = item.subItems.map((subItem) => {
            const filteredMenus = (subItem.menus || []).filter((menu) => {
              if (!menu.featureKey) return true;
              return effectiveFeatures?.[menu.featureKey] !== false;
            });
            return filteredMenus.length > 0 ? { ...subItem, menus: filteredMenus } : null;
          }).filter(Boolean);
          return { ...item, subItems: filteredSubItems };
        }
      }

      // MCP Security — gated entirely on the mcp_security SKU + mcp.*
      // feature keys. If every menu is licensed-off (i.e. the org holds
      // no mcp_security grant), the whole MCP module disappears from the
      // sidebar. Pre-load, we keep the module visible to avoid a flash.
      if (item.title === "MCP Security") {
        // Super admin bypass — platform-level access shows the full module.
        if (sauser) return item;
        if (effectiveFeaturesLoaded) {
          // Fail-closed: MCP is a licensed module, so a missing/unknown
          // feature-key value is treated the same as explicit false
          // (hidden). This differs from API Security's pattern — a
          // missing mcp_security SKU should hide the entire module from
          // the sidebar + render the lock page on direct URL access.
          const filteredSubItems = item.subItems.map((subItem) => {
            const filteredMenus = (subItem.menus || []).filter((menu) => {
              if (!menu.featureKey) return true;
              return effectiveFeatures?.[menu.featureKey] === true;
            });
            return filteredMenus.length > 0 ? { ...subItem, menus: filteredMenus } : null;
          }).filter(Boolean);
          if (filteredSubItems.length === 0) return null;
          return { ...item, subItems: filteredSubItems };
        }
        // While loading, hide — avoids a flash of the module for
        // unlicensed users (safer default for a licensed module).
        return null;
      }

      // Overview is always visible
      return item;
    }).filter(Boolean); // Remove null items
  }, [hasPermission, hasRolePermission, isAdmin, PERMISSIONS, effectiveFeatures, effectiveFeaturesLoaded]);

  useEffect(() => {
    if (projectList === null) return;
    setProjects([{ id: "all", name: "All projects" }, { id: "default", name: "Default" }, ...projectList]);
  }, [projectList]);

  const [structedProjects, setStructedProjects] = useState(null);
  const [openProjectIds, setOpenProjectIds] = useState([]);

  useEffect(() => {
    if (structedProjectList === null) return;
    setStructedProjects(structedProjectList);
  }, [structedProjectList]);

  // Fetch organisation settings for display name and tenant indicator
  useEffect(() => {
    const fetchOrganisationSettings = async () => {
      try {
        const response = await userAxios.get("/organisation/settings");
        const settings = response.data;
        if (settings.displayName !== undefined) {
          setOrganisationDisplayName(settings.displayName || "My Organisation");
        }
        if (settings.showHeaderIndicator !== undefined) {
          setShowTenantIndicator(settings.showHeaderIndicator);
        }
      } catch (err) {
        console.error("Error fetching organisation settings:", err);
        // Keep default value on error
      }
    };

    fetchOrganisationSettings();

    // Listen for settings updates
    const handleSettingsUpdate = () => {
      fetchOrganisationSettings();
    };

    window.addEventListener("organisationSettingsUpdated", handleSettingsUpdate);

    return () => {
      window.removeEventListener("organisationSettingsUpdated", handleSettingsUpdate);
    };
  }, []);

  const filteredStructuredProjectList = useMemo(() => {
    return structedProjects
      ?.filter((project) => {
        if (project?.subprojects) {
          return (
            project?.name?.toLowerCase()?.includes(pattern?.toLowerCase()) ||
            project.subprojects?.some((subproject) => subproject?.name?.toLowerCase()?.includes(pattern?.toLowerCase()))
          );
        } else {
          return project?.name?.toLowerCase()?.includes(pattern?.toLowerCase());
        }
      })
      .map((project) => {
        if (project?.subprojects) {
          return {
            ...project,
            subprojects: project.subprojects?.filter((subproject) => subproject?.name?.toLowerCase()?.includes(pattern?.toLowerCase())),
          };
        } else {
          return project;
        }
      });
  }, [pattern, structedProjects]);

  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const accountMenuOpen = Boolean(accountAnchorEl);
  const handleAccountMenuOpen = (event) => setAccountAnchorEl(event.currentTarget);
  const handleAccountMenuClose = () => setAccountAnchorEl(null);

  // Global search modal — opened by clicking the top-bar search trigger or
  // pressing ⌘K / Ctrl+K from anywhere in the app.
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Bug-report drawer + lazy installation of the global console-error
  // ring buffer the drawer attaches to outbound reports.
  const [openBugReport, setOpenBugReport] = useState(false);
  const [openReleaseNotes, setOpenReleaseNotes] = useState(false);
  useEffect(() => { installConsoleErrorBuffer(); }, []);

  const [openNotification, setOpenNotification] = useState(false);
  const handleNotificationOpen = () => {
    setOpenNotification(true);
    // Refresh on open so the panel shows what's actually there.
    if (typeof getNotifications4User === "function") getNotifications4User();
  };
  const handleNotificationClose = () => setOpenNotification(false);

  const [selectedNotificationTab, setSelectedNotificationTab] = useState(NotificationTabs.Unread);

  // Initial fetch on mount.
  useEffect(() => {
    if (typeof getNotifications4User === "function") getNotifications4User();
  }, [getNotifications4User]);

  // Map backend Notification rows -> the shape this panel renders.
  // Backend: { id, title, content, type, created, read }
  // Panel:   { idx, title, subTitle, content, time, unread, _id }
  // subTitle is no longer fixture text — we surface the alert type/category
  // when present, or leave blank.
  const notificationList = useMemo(() => {
    if (!Array.isArray(notifications)) return [];
    return notifications.map((n, i) => ({
      idx: n.id || i,
      _id: n.id,
      title: n.title || "Notification",
      subTitle: n.type && n.type !== "DEFAULT" ? String(n.type).replace(/_/g, " ") : "",
      content: n.content || "",
      time: _relativeTime(n.created),
      unread: !n.read,
    }));
  }, [notifications]);

  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [currentMainMenu, setCurrentMainMenu] = useState(null);
  const [hoveredNavTitle, setHoveredNavTitle] = useState(null);
  const subMenuCloseTimerRef = useRef(null);

  const clearSubMenuCloseTimer = useCallback(() => {
    if (subMenuCloseTimerRef.current) {
      clearTimeout(subMenuCloseTimerRef.current);
      subMenuCloseTimerRef.current = null;
    }
  }, []);

  const scheduleSubMenuClose = useCallback(() => {
    clearSubMenuCloseTimer();
    subMenuCloseTimerRef.current = window.setTimeout(() => {
      setOpenSubMenu(false);
      setHoveredNavTitle(null);
    }, 120);
  }, [clearSubMenuCloseTimer]);

  const closeSubMenu = useCallback(() => {
    clearSubMenuCloseTimer();
    setOpenSubMenu(false);
    setHoveredNavTitle(null);
  }, [clearSubMenuCloseTimer]);

  const handleSidebarItemHover = useCallback((item, prefetchHandler) => {
    clearSubMenuCloseTimer();
    setHoveredNavTitle(item.title);
    prefetchHandler?.();
    if (item.subItems?.length > 0) {
      setCurrentMainMenu(item);
      setOpenSubMenu(true);
    } else {
      setOpenSubMenu(false);
    }
  }, [clearSubMenuCloseTimer]);

  useEffect(() => {
    const menu = filteredItems.find((item) => item.path && currentRoute.startsWith(item.path));
    setCurrentMainMenu(menu || null);
  }, [currentRoute, filteredItems]);

  useEffect(() => () => clearSubMenuCloseTimer(), [clearSubMenuCloseTimer]);

  const currentSubMenus = useMemo(() => {
    if (!currentMainMenu) return [];
    const found = filteredItems.find((item) => item.title === currentMainMenu.title);
    return found ? found.subItems : [];
  }, [currentMainMenu, filteredItems]);

  const [projectAnchorEl, setProjectAnchorEl] = useState(null);
  const projectMenuOpen = Boolean(projectAnchorEl);
  const handleProjectMenuOpen = (event) => setProjectAnchorEl(event.currentTarget);
  const handleProjectMenuClose = () => {
    setPattern("");
    setProjectAnchorEl(null);
  };

  return (
    <HowToWorkProvider>
    <>
      <Box sx={{ width: "100vw", height: "100vh", backgroundColor: "#FFFFFF" }}>
        <Stack direction="row">
          <Stack
            direction="column"
            spacing={0}
            alignItems={"center"}
            onMouseLeave={scheduleSubMenuClose}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: 64,
              height: "100vh",
              borderRight: "1px solid #D4D4D4",
              backgroundColor: "#FFFFFF",
              zIndex: 1000,
              overflow: "hidden",
            }}
          >
            <img
              src={LogoIcon}
              alt="Logo"
              style={{ width: 34, height: "auto", marginTop: 16, marginBottom: 24, flexShrink: 0 }}
            />
            <Stack
              direction="column"
              alignItems="center"
              spacing={6}
              onScroll={closeSubMenu}
              sx={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                pb: 3,
                // Thin theme-aligned scrollbar (visible affordance on short viewports)
                scrollbarWidth: "thin",
                scrollbarColor: `${colors.neutral[300]} transparent`,
                "&::-webkit-scrollbar": {
                  width: 1,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: colors.neutral[300],
                  borderRadius: 1,
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: colors.neutral[400],
                },
              }}
            >
              {filteredItems.map((item, idx) => {
                const prefetchHandler = getPrefetchHandler(item.title);
                const isRouteActive = isMainNavRouteActive(currentRoute, item.path);
                const isFlyoutOpen = openSubMenu && currentMainMenu?.title === item.title;
                const isHovered = hoveredNavTitle === item.title;
                return (
                <Stack
                  key={`sidebar-item-${idx}`}
                  className="sidebar-nav-item"
                  spacing={1.25}
                  alignItems="center"
                  onMouseEnter={() => handleSidebarItemHover(item, prefetchHandler)}
                  onClick={() => {
                    if (item.subItems.length === 0) {
                      navigate(item.path);
                      closeSubMenu();
                    }
                  }}
                  sx={getSidebarNavItemSx({ isRouteActive, isFlyoutOpen, isHovered })}
                >
                  <SidebarNavIcon
                    icon={item.icon}
                    active={isRouteActive || isFlyoutOpen || isHovered}
                  />
                  <Typography
                    className="sidebar-nav-label"
                    sx={{
                      color: isRouteActive || isFlyoutOpen || isHovered
                        ? colors.primary[300]
                        : SIDEBAR_NAV_IDLE_COLOR,
                      fontSize: 12,
                      lineHeight: 1.2,
                      fontWeight: "medium",
                      textAlign: "center",
                      width: "100%",
                      wordBreak: "break-word",
                      transition: "color 120ms ease",
                    }}
                  >
                    {item.title}
                  </Typography>
                </Stack>
              );
              })}
            </Stack>
          </Stack>
          <Stack
            direction="column"
            onMouseEnter={closeSubMenu}
            sx={{ position: "relative", top: 0, left: 64, width: `calc(100% - 64px)`, height: "100vh" }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={"space-between"}
              spacing={8}
              sx={{
                position: "fixed",
                top: 0,
                left: 64,
                backgroundColor: "#FFFFFF",
                width: `calc(100% - 64px)`,
                height: 66,
                borderBottom: "1px solid #D4D4D4",
                px: 6,
                zIndex: 1000,
              }}
            >
              <Stack direction={"row"} alignItems={"center"} spacing={4}>
                {showTenantIndicator && (
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  spacing={1.25}
                  sx={{
                    // width: 156,
                    height: 32,
                    backgroundColor: "rgba(2, 85, 237, 0.07)",
                    borderRadius: "8px",
                    py: 1,
                    pr: 4,
                    pl: 2,
                    cursor: "pointer",
                  }}
                >
                  <LayerdIcon icon={Building2} size={20} color="primaryLight" />
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: "21px",
                      fontWeight: "medium",
                      color: colors.primary[300],
                    }}
                  >
                   {organisationDisplayName}
                  </Typography>
                </Stack>
                )}
                {projects?.length > 0 && (
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    spacing={1.25}
                    onClick={handleProjectMenuOpen}
                    sx={{
                      width: 160,
                      minWidth: 160,
                      maxWidth: 160,
                      height: 32,
                      borderRadius: "8px",
                      py: 1,
                      px: 2,
                      cursor: "pointer",
                      backgroundColor: projectMenuOpen ? colors.primary.alpha : "transparent",
                      "& svg": {
                        color: colors.primary[300],
                        flexShrink: 0,
                      },
                      "& .MuiTypography-root": {
                        color: colors.primary[300],
                      },
                      "&:hover": {
                        backgroundColor: colors.primary.alpha,
                      },
                    }}
                  >
                    <LayerdIcon icon={FolderKanban} size={16} color="inherit" />
                    <Typography
                      noWrap
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 14,
                        lineHeight: "21px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {projects?.find((project) => project?.id === curProjectId)?.name}
                    </Typography>
                    {projectMenuOpen ? (
                      <LayerdIcon icon={ChevronUp} size={16} color="inherit" />
                    ) : (
                      <LayerdIcon icon={ChevronDown} size={16} color="inherit" />
                    )}
                  </Stack>
                )}
                {!isSiteSelectorHiddenPath(currentRoute) && <GlobalSiteSelector />}
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ flexShrink: 0, height: 32 }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  onClick={() => setSearchOpen(true)}
                  sx={{
                    width: 320,
                    height: 32,
                    flexShrink: 0,
                    borderRadius: "8px",
                    border: `1px solid ${colors.neutral[300]}`,
                    backgroundColor: "#fff",
                    px: 1.5,
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: colors.neutral[400],
                      backgroundColor: colors.neutral[50],
                    },
                  }}
                >
                  <LayerdIcon icon={Search} size={16} color="neutral" />
                  <Typography sx={{
                    flex: 1, fontSize: 14, color: colors.neutral[500],
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    Search domains, rules, certs, IPs, paths…
                  </Typography>
                  <Box sx={{
                    fontFamily: "monospace", fontSize: 11, color: colors.neutral[600],
                    border: `1px solid ${colors.neutral[300]}`, borderRadius: "4px",
                    padding: "1px 6px", backgroundColor: colors.neutral[50],
                  }}>⌘K</Box>
                </Stack>
                <HowToWorkHeaderTrigger />
                <Box
                  component="button"
                  type="button"
                  onClick={handleMenuOpen}
                  aria-label="Help"
                  sx={{
                    width: 32,
                    height: 32,
                    p: 0,
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    backgroundColor: menuOpen ? colors.primary[100] : "transparent",
                    "&:hover": {
                      backgroundColor: colors.primary[100],
                      "& svg": { color: colors.primary[300] },
                    },
                    "& svg": {
                      color: menuOpen ? colors.primary[300] : colors.neutral[600],
                    },
                  }}
                >
                  <LayerdIcon icon={CircleHelp} size={20} color="inherit" />
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setOpenBugReport(true)}
                  aria-label="Report a bug"
                  title="Report a bug"
                  sx={{
                    width: 32,
                    height: 32,
                    p: 0,
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: colors.primary[100],
                      "& svg": { color: colors.primary[300] },
                    },
                    "& svg": { color: colors.neutral[600] },
                  }}
                >
                  <LayerdIcon icon={Bug} size={20} color="inherit" />
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleNotificationOpen}
                  aria-label="Notifications"
                  sx={{
                    width: 32,
                    height: 32,
                    p: 0,
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: colors.primary[100],
                      "& svg": { color: colors.primary[300] },
                    },
                    "& svg": { color: colors.neutral[600] },
                  }}
                >
                  <Badge
                    badgeContent={notificationList?.filter((noti) => noti.unread).length}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: 10,
                        lineHeight: "14px",
                        fontWeight: 500,
                        color: colors.neutral[1000],
                        height: 18,
                        minWidth: 18,
                        px: 0.5,
                        borderRadius: "100%",
                        backgroundColor: colors.secondary[300],
                      },
                    }}
                    color="primary"
                  >
                    <LayerdIcon icon={Bell} size={20} color="inherit" />
                  </Badge>
                </Box>
                <Avatar
                  {...stringAvatar(user?.username)}
                  onClick={handleAccountMenuOpen}
                  sx={{
                    bgcolor: colors.primary[500],
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    fontSize: 14,
                    fontWeight: "medium",
                    lineHeight: "21px",
                    cursor: "pointer",
                  }}
                />
              </Stack>
            </Stack>
            <Stack
              direction="column"
              sx={{
                position: "fixed",
                top: 66,
                left: 64,
                width: "calc(100% - 64px)",
                height: "calc(100vh - 66px)",
                zIndex: 500,
              }}
            >
              <HowToWorkPanel />
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <ReactPerfectScrollbar
                  containerRef={(ref) => { mainScrollRef.current = ref; }}
                  style={{ height: "100%" }}
                >
                  <Box>
                    <LicenseBreachBanner />
                    {children}
                    <Outlet />
                  </Box>
                </ReactPerfectScrollbar>
              </Box>
            </Stack>
          </Stack>
          <Slide direction="right" in={openSubMenu} mountOnEnter unmountOnExit>
            <Stack
              onMouseEnter={clearSubMenuCloseTimer}
              onMouseLeave={scheduleSubMenuClose}
              sx={{
                position: "absolute",
                top: 66,
                left: 64,
                height: `calc(100vh - 66px)`,
                width: 260,
                backgroundColor: colors.neutral[100],
                borderRight: `solid 1px ${colors.neutral[200]}`,
                zIndex: 999,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ReactPerfectScrollbar style={{ flex: 1 }}>
                <Stack spacing={6} sx={{ p: 4 }}>
                  {currentSubMenus?.map((submenu, index) => (
                    <Box key={`sub_menu_${index}`}>
                      <Typography sx={{
                        fontSize: 13,
                        lineHeight: "20px",
                        fontWeight: 600,
                        color: colors.neutral[1000],
                        pb: 2,
                        letterSpacing: 0.02,
                      }}>
                        {submenu?.title}
                      </Typography>
                      <Stack spacing={0}>
                        {submenu?.menus?.map((subItem, index2) => {
                          const isActive = isFlyoutMenuActive(currentRoute, subItem.path);
                          return (
                            <Stack
                              key={`sub_menu_${index}_${index2}`}
                              direction={"row"}
                              alignItems={"center"}
                              spacing={2}
                              onClick={() => {
                                navigate(subItem.path);
                                closeSubMenu();
                              }}
                              sx={{
                                width: "100%",
                                minHeight: 32,
                                px: 1,
                                py: 1,
                                cursor: "pointer",
                                borderRadius: "8px",
                                transition: "background-color 120ms ease, color 120ms ease",
                                ...(isActive
                                  ? { backgroundColor: colors.primary.alpha }
                                  : {}),
                                "&:hover": {
                                  backgroundColor: colors.neutral[50],
                                  "& .MuiTypography-root": {
                                    color: colors.primary[300],
                                  },
                                  "& svg": {
                                    color: colors.primary[300],
                                  },
                                },
                                "& svg": {
                                  color: isActive ? colors.primary[300] : SIDEBAR_NAV_IDLE_COLOR,
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  lineHeight: 0,
                                }}
                              >
                                <SidebarNavIcon
                                  icon={subItem.icon}
                                  active={isActive}
                                  size={15}
                                />
                              </Box>
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: 14,
                                  lineHeight: "18px",
                                  m: 0,
                                  color: isActive ? colors.primary[300] : SIDEBAR_SUBMENU_IDLE_COLOR,
                                }}
                              >
                                {subItem.title}
                              </Typography>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </ReactPerfectScrollbar>
            </Stack>
          </Slide>
        </Stack>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 10,
          left: -8,
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
          "& .MuiList-root": {
            px: 3,
            py: 3,
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
          },
        }}
      >
        <Stack
          component="a"
          href={DOCUMENTATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          onClick={handleMenuClose}
          sx={{ ...generateMenuSx(150), textDecoration: "none", color: "inherit" }}
        >
          <LayerdIcon icon={BookOpen} size={20} color="neutral" />
          <Typography sx={{ fontSize: 14, lineHeight: "20px", pl: 1, color: colors.neutral[700] }}>
            Documentation
          </Typography>
        </Stack>
        <Stack
          component="button"
          type="button"
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          onClick={() => {
            handleMenuClose();
            setOpenReleaseNotes(true);
          }}
          sx={{
            ...generateMenuSx(150),
            textDecoration: "none",
            color: "inherit",
            border: "none",
            background: "none",
            font: "inherit",
            textAlign: "left",
          }}
        >
          <LayerdIcon icon={ClipboardList} size={20} color="neutral" />
          <Typography sx={{ fontSize: 14, lineHeight: "20px", pl: 1, color: colors.neutral[700] }}>
            Release notes
          </Typography>
        </Stack>
      </Menu>
      <Menu
        anchorEl={projectAnchorEl}
        open={projectMenuOpen}
        onClose={handleProjectMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 10,
          left: 0,
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
          "& .MuiList-root": {
            px: 3,
            py: 3,
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
          },
        }}
      >
        <Box sx={{ width: 300 }}>
          <Stack spacing={3}>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={3}>
              <Typography sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}>
                Project scope
              </Typography>
              <Stack
                direction={"row"}
                alignItems={"center"}
                spacing={2}
                onClick={() => {
                  navigate("/v2/settings/project");
                  handleProjectMenuClose();
                }}
                sx={{ backgroundColor: colors.primary["alpha"], borderRadius: "8px", height: 26, px: 3, cursor: "pointer" }}
              >
                <LayerdIcon icon={Plus} size={18} color="primaryLight" />
                <Typography sx={ { fontSize: 13, lineHeight: "18px", color: colors.primary[300] }}>
                  Add project
                </Typography>
              </Stack>
            </Stack>
            <OutlinedInput
              startAdornment={<LayerdIcon icon={Search} size={16} color="neutral" />}
              placeholder="Search projects"
              fullWidth
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              sx={{
                height: 32,
                borderRadius: "8px",
                fontSize: 14,
                lineHeight: "21px",
                borderColor: colors.neutral[600],
                pl: 2,
                "& .MuiOutlinedInput-input": {
                  fontSize: 14,
                  lineHeight: "21px",
                  px: 2,
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: 14,
                  lineHeight: "21px",
                },
              }}
            />
            {projectList === null ? (
              <Stack alignItems={"center"} justifyContent={"center"} sx={{ width: "100%", height: 200 }}>
                <CircularProgress sx={{ color: colors.neutral[400] }} />
              </Stack>
            ) : (
              <ReactPerfectScrollbar style={{ maxHeight: "240px" }}>
                <Stack>
                  {filteredStructuredProjectList?.map((project) => [
                    <Stack
                      key={`project_${project?.id}`}
                      direction={"row"}
                      alignItems={"center"}
                      spacing={1}
                      onClick={() => {
                        setCurProjectId(project?.id);
                        handleProjectMenuClose();
                      }}
                      sx={{
                        ...generateMenuSx("100%"),
                        ...(curProjectId === project?.id && { backgroundColor: colors.primary.alpha }),
                      }}
                    >
                      <LayerdIcon
                        icon={FolderKanban}
                        size={18}
                        color={curProjectId === project?.id ? "primaryLight" : "neutral"}
                      />
                      <Typography
                        sx={{
                          fontSize: 14,
                          lineHeight: "20px",
                          pl: 1,
                          color: curProjectId === project?.id ? colors.primary[300] : colors.neutral[700],
                        }}
                      >
                        {project?.name}
                      </Typography>
                      {project?.subprojects?.length > 0 && (
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            if (openProjectIds?.includes(project?.id)) {
                              setOpenProjectIds((prev) => prev?.filter((i) => i !== project?.id));
                            } else {
                              setOpenProjectIds((prev) => [...prev, project?.id]);
                            }
                          }}
                          sx={{ width: 24, height: 24 }}
                        >
                          {openProjectIds?.includes(project?.id) ? (
                            <LayerdIcon icon={ChevronUp} size={16} color="neutral" />
                          ) : (
                            <LayerdIcon icon={ChevronDown} size={16} color="neutral" />
                          )}
                        </Box>
                      )}
                    </Stack>,
                    <Collapse in={openProjectIds?.includes(project?.id)} timeout="auto" unmountOnExit>
                      <Box sx={{ pl: 4 }}>
                        {project?.subprojects?.map((subproject) => (
                          <Stack
                            key={`project_${subproject?.id}`}
                            direction={"row"}
                            alignItems={"center"}
                            spacing={1}
                            onClick={() => {
                              setCurProjectId(subproject?.id);
                              handleProjectMenuClose();
                            }}
                            sx={{
                              ...generateMenuSx("100%"),
                              ...(curProjectId === subproject?.id && { backgroundColor: colors.primary.alpha }),
                            }}
                          >
                            <LayerdIcon
                              icon={FolderKanban}
                              size={18}
                              color={curProjectId === subproject?.id ? "primaryLight" : "neutral"}
                            />
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "20px",
                                pl: 1,
                                color: curProjectId === subproject?.id ? colors.primary[300] : colors.neutral[700],
                              }}
                            >
                              {subproject?.name}
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                    </Collapse>,
                  ])}
                </Stack>
                {filteredStructuredProjectList?.length === 0 && (
                  <Typography sx={ { fontSize: 14, lineHeight: "21px" }}>No projects</Typography>
                )}
              </ReactPerfectScrollbar>
            )}
          </Stack>
        </Box>
      </Menu>
      <Menu
        anchorEl={accountAnchorEl}
        open={accountMenuOpen}
        onClose={handleAccountMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 10,
          left: -8,
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
          "& .MuiList-root": {
            px: 3,
            py: 3,
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
          },
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          onClick={() => {
            navigate("/v2/settings/account");
            handleAccountMenuClose();
          }}
          sx={generateMenuSx(200)}
        >
          <LayerdIcon icon={User} size={18} color="neutral" />
          <Typography sx={ { fontSize: 14, lineHeight: "20px", pl: 1, color: colors.neutral[700] }}>
            Account management
          </Typography>
        </Stack>
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          onClick={() => {
            navigate("/v2/settings/organisation");
            handleAccountMenuClose();
          }}
          sx={generateMenuSx(200)}
        >
          <LayerdIcon icon={Building2} size={18} color="neutral" />
          <Typography sx={ { fontSize: 14, lineHeight: "20px", pl: 1, color: colors.neutral[700] }}>
            Organisation settings
          </Typography>
        </Stack>
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          onClick={() => {
            navigate("/v2/settings/license");
            handleAccountMenuClose();
          }}
          sx={generateMenuSx(200)}
        >
          <LayerdIcon icon={LicenseIcon} size={18} color="neutral" />
          <Typography sx={{ fontFamily: "Rubik", fontSize: 14, lineHeight: "20px", pl: 1, color: colors.neutral[700] }}>
            License
          </Typography>
        </Stack>
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          onClick={() => {
            signOut();
            handleAccountMenuClose();
          }}
          sx={generateMenuSx(200)}
        >
          <LayerdIcon icon={LogOut} size={18} color="neutral" />
          <Typography sx={ { fontSize: 14, lineHeight: "20px", pl: 1, color: colors.neutral[700] }}>Logout</Typography>
        </Stack>
      </Menu>
      <Modal open={openNotification}>
        <Slide direction="left" in={openNotification} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 480,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
            }}
          >
            <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
              <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                <LayerdIcon icon={Bell} size={20} color="neutralDark" />
                <Typography
                  sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                >
                  Notifications
                </Typography>
              </Stack>
              <Box onClick={handleNotificationClose} sx={{ cursor: "pointer" }}>
                <LayerdIcon icon={X} size={20} color="neutral" />
              </Box>
            </Stack>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={3} sx={{ px: 4 }}>
              <Tabs
                value={selectedNotificationTab}
                onChange={(_, v) => setSelectedNotificationTab(v)}
                sx={{
                  minHeight: "inherit",
                  height: 32,
                  "& .MuiTabs-flexContainer": { border: "none", px: 0, py: 0, gap: "8px", height: 32 },
                  "& .MuiTabs-indicator": { height: "1px", backgroundColor: colors.secondary[300] },
                  "& button": { p: 0, minWidth: "inherit", minHeight: "inherit" },
                  "& button .MuiTypography-root": { color: colors.neutral[700] },
                  "& .Mui-selected .MuiTypography-root": { color: colors.neutral[1000] },
                }}
              >
                <Tab
                  value={NotificationTabs.Unread}
                  label={
                    <Stack direction={"row"} alignItems={"center"} spacing={4} sx={{ pl: 4, pr: 6, py: 2 }}>
                      <Typography sx={ { fontSize: 14 }}>Unread</Typography>
                      <Badge
                        badgeContent={notificationList?.filter((noti) => noti.unread).length}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: 10,
                            fontWeight: "regular",
                            color: colors.neutral[1000],
                            height: 18,
                            minWidth: 18,
                            borderRadius: "100%",
                            backgroundColor: colors.secondary[300],
                          },
                        }}
                      />
                    </Stack>
                  }
                />
                <Tab
                  value={NotificationTabs.Read}
                  label={<Typography sx={ { fontSize: 14 }}>Read</Typography>}
                  sx={{ width: 65 }}
                />
                <Tab
                  value={NotificationTabs.All}
                  label={<Typography sx={ { fontSize: 14 }}>All</Typography>}
                  sx={{ width: 50 }}
                />
              </Tabs>
              <Stack
                direction={"row"}
                alignItems={"center"}
                spacing={1}
                onClick={() => {
                  if (typeof markAllNotificationAsRead === "function") markAllNotificationAsRead();
                }}
                sx={{ cursor: "pointer", height: 24 }}
              >
                <LayerdIcon icon={Check} size={18} color="primaryLight" />
                <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                  Mark all as read
                </Typography>
              </Stack>
            </Stack>
            <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 130px)` }}>
              <ReactPerfectScrollbar>
                <Stack spacing={2} sx={{ pr: 4, height: "100%" }}>
                  {notificationList
                    ?.filter((noti) => {
                      if (selectedNotificationTab === NotificationTabs.Unread) {
                        return noti.unread;
                      } else if (selectedNotificationTab === NotificationTabs.Read) {
                        return !noti.unread;
                      } else {
                        return true;
                      }
                    })
                    ?.map((noti) => {
                      return (
                        <Stack
                          key={`notification_unread_${noti.idx}`}
                          sx={{ borderRadius: "8px", px: 2, py: 3, "&:hover": { backgroundColor: colors.primary["alpha"] } }}
                        >
                          <Stack direction={"row"} alignItems={"flex-start"} spacing={2}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "100%",
                                backgroundColor: noti.unread ? colors.secondary[300] : "transparent",
                                mt: 1.5,
                              }}
                            />
                            <Stack spacing={2} sx={{ width: `calc(100% - 16px)` }}>
                              <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={2}>
                                <Typography
                                  sx={{
                                    fontWeight: "medium",
                                    fontSize: 14,
                                    lineHeight: "20px",
                                    color: colors.neutral[1000],
                                  }}
                                >
                                  {noti?.title}
                                </Typography>
                                <Stack
                                  alignItems={"center"}
                                  justifyContent={"center"}
                                  onClick={() => {
                                    // The X icon now means "mark as read" — backend
                                    // has no delete endpoint per-user (read-state only).
                                    if (noti._id && typeof markNotificationAsRead === "function") {
                                      markNotificationAsRead(noti._id);
                                    }
                                  }}
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "100%",
                                    cursor: "pointer",
                                    "&:hover": { backgroundColor: colors.neutral[200] },
                                  }}
                                >
                                  <LayerdIcon icon={X} size={14} color="neutral" />
                                </Stack>
                              </Stack>
                              <Stack spacing={1}>
                                <Typography
                                  sx={{
                                    fontWeight: "medium",
                                    fontSize: 12,
                                    lineHeight: "18px",
                                    color: colors.neutral[700],
                                  }}
                                >
                                  {noti.subTitle}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    lineHeight: "18px",
                                    color: colors.neutral[700],
                                  }}
                                >
                                  {noti.content}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    lineHeight: "18px",
                                    color: colors.neutral[700],
                                  }}
                                >
                                  {noti.time}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Stack>
                      );
                    })}
                  {notificationList?.filter((noti) => {
                    if (selectedNotificationTab === NotificationTabs.Unread) {
                      return noti.unread;
                    } else if (selectedNotificationTab === NotificationTabs.Read) {
                      return !noti.unread;
                    } else {
                      return true;
                    }
                  })?.length === 0 && (
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[600], mt: 4 }}>
                      No notifications
                    </Typography>
                  )}
                </Stack>
              </ReactPerfectScrollbar>
            </Box>
          </Stack>
        </Slide>
      </Modal>

      <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      <HowToWorkVideoModal />
      <ReportBugDrawer
        open={openBugReport}
        onClose={() => setOpenBugReport(false)}
        onSubmitted={(report) => showSuccess(
          `Bug report submitted (id: ${report.id.slice(0, 8)}…). Our team will follow up by email.`
        )}
      />
      <ReleaseNotesDrawer
        open={openReleaseNotes}
        onClose={() => setOpenReleaseNotes(false)}
      />
    </>
    </HowToWorkProvider>
  );
};
export default NewNormalLayout;
